import React, { useState, useMemo, useEffect } from 'react';
import CustomTable from './CustomTable';
import SaleTicket from './SaleTicket';
import { salesAPI } from '../services/api';
import { generateSalePDF } from './SalePDF';
import ActionModal from './ActionModal';
import Toast from './Toast';
import { isAdmin, isEncargada } from '../hooks/useSucursal';
import './SalesHistory.css';

// ─────────────────────────────────────────────────────────────────────────────
// Modal genérico para las 3 acciones del flujo de anulación:
//   'solicitar' (Encargada, motivo obligatorio) | 'aprobar' (Dueño, sin motivo)
//   'rechazar' (Dueño, motivo obligatorio)
// ─────────────────────────────────────────────────────────────────────────────
const AnulacionActionModal = ({ mode, sale, motivo, setMotivo, onConfirm, onClose, loading }) => {
    const config = {
        solicitar: {
            titulo: '📝 Solicitar Anulación',
            color: '#f59e0b',
            texto: 'Esto NO anula la venta todavía ni toca el stock — queda pendiente de que el Dueño la apruebe o la rechace.',
            requiereMotivo: true,
            placeholder: 'Ej: Cliente se arrepintió, producto sin abrir...',
            confirmLabel: 'Enviar solicitud'
        },
        aprobar: {
            titulo: '✅ Aprobar Anulación',
            color: '#16a34a',
            texto: 'El stock de todos los productos será restaurado y la venta quedará cancelada.',
            requiereMotivo: false,
            confirmLabel: 'Sí, aprobar y restaurar stock'
        },
        rechazar: {
            titulo: '❌ Rechazar Anulación',
            color: '#dc2626',
            texto: 'La venta vuelve a su estado "entregado" tal cual estaba. La Encargada verá el motivo del rechazo.',
            requiereMotivo: true,
            placeholder: 'Ej: El producto ya fue consumido, no aplica devolución...',
            confirmLabel: 'Rechazar solicitud'
        }
    }[mode];

    const displayId = typeof sale.correlativo_local === 'number'
        ? `VTA-${String(sale.correlativo_local).padStart(5, '0')}`
        : (sale.correlativo_local || sale.id);

    const disabled = loading || (config.requiereMotivo && !motivo.trim());

    return (
        <div className="modal-overlay" style={{ zIndex: 999999 }} onClick={onClose}>
            <div className="modal-content" style={{ maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
                <div className="modal-header" style={{ borderBottom: `3px solid ${config.color}` }}>
                    <h2 style={{ color: config.color, fontSize: '1.1rem' }}>{config.titulo}</h2>
                    <button className="close-button" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    <p style={{ margin: '0 0 12px 0', color: '#1e293b', fontWeight: '600' }}>
                        Venta <span style={{ color: config.color }}>{displayId}</span> — S/. {parseFloat(sale.total_amount).toFixed(2)}
                    </p>
                    <p style={{ margin: '0 0 16px 0', color: '#64748b', fontSize: '0.9rem', lineHeight: '1.5' }}>
                        {config.texto}
                    </p>

                    {config.requiereMotivo && (
                        <textarea
                            value={motivo}
                            onChange={(e) => setMotivo(e.target.value)}
                            placeholder={config.placeholder}
                            rows={3}
                            style={{
                                width: '100%', boxSizing: 'border-box', padding: '10px',
                                border: '1px solid #cbd5e1', borderRadius: '8px',
                                fontSize: '0.9rem', resize: 'vertical', marginBottom: '20px'
                            }}
                        />
                    )}

                    {mode === 'solicitar' && sale.motivo_rechazo && (
                        <p style={{ margin: '-8px 0 16px', fontSize: '0.85rem', color: '#dc2626', background: '#fef2f2', padding: '8px 10px', borderRadius: '6px' }}>
                            ⚠️ Una solicitud anterior fue rechazada. Motivo: {sale.motivo_rechazo}
                        </p>
                    )}

                    <div style={{ display: 'flex', gap: '12px' }}>
                        <button
                            onClick={onClose}
                            disabled={loading}
                            style={{
                                flex: 1, padding: '11px', border: '1px solid #cbd5e1',
                                background: 'white', color: '#475569', fontWeight: '600',
                                borderRadius: '8px', cursor: 'pointer', fontSize: '0.95rem'
                            }}
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={disabled}
                            style={{
                                flex: 1, padding: '11px', border: 'none',
                                background: disabled ? '#cbd5e1' : config.color,
                                color: 'white', fontWeight: '700',
                                borderRadius: '8px', cursor: disabled ? 'not-allowed' : 'pointer',
                                fontSize: '0.95rem'
                            }}
                        >
                            {loading ? 'Procesando...' : config.confirmLabel}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const SaleDetailsModal = ({ sale, onClose }) => {
    if (!sale) return null;

    const isPending = sale.payment_status?.toLowerCase() === 'pendiente';
    const isAnulada = sale.estado === 'ANULADA' || sale.order_status === 'cancelado';
    const isPendAnulacion = sale.estado === 'PENDIENTE_ANULACION' || sale.order_status === 'pendiente_anulacion';

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content sale-details-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Detalles de Venta #{typeof sale.correlativo_local === 'number' ? `VTA-${String(sale.correlativo_local).padStart(5, '0')}` : (sale.correlativo_local || sale.id)}</h3>
                    <button className="close-modal-btn" onClick={onClose}>✕</button>
                </div>
                
                <div className="modal-body">
                    <div className="sale-info-grid">
                        <div>
                            <strong>Fecha:</strong> {new Date(sale.created_at || sale.sale_date).toLocaleDateString('es-PE')}
                        </div>
                        <div>
                            <strong>Cliente:</strong> {sale.customer_name || sale.Customer?.name || sale.notes?.match(/Cliente:\s*(.+)/i)?.[1]?.trim() || 'Consumidor Final'}
                        </div>
                        <div>
                            <strong>Sucursal:</strong> {sale.sucursal_nombre || 'Principal'}
                        </div>
                        <div>
                            <strong>Método de Pago:</strong>{' '}
                            {sale.payment_method === 'efectivo' ? '💵 Efectivo' :
                             sale.payment_method === 'tarjeta' ? '💳 Tarjeta' :
                             sale.payment_method === 'transferencia' ? '🏦 Transferencia' :
                             sale.payment_method === 'yape' ? '📱 Yape/Plin' :
                             sale.payment_method === 'fiado' ? '📝 Fiado' :
                             'No especificado'}
                        </div>
                    </div>

                    {isPendAnulacion && (
                        <div style={{ margin: '15px 0', padding: '12px', background: '#fef3c7', borderLeft: '4px solid #f59e0b', borderRadius: '6px', fontSize: '0.9rem' }}>
                            <strong style={{ color: '#b45309' }}>⏳ Solicitud de anulación en curso</strong>
                            <p style={{ margin: '4px 0 0 0', color: '#92400e' }}>
                                <strong>Solicitante:</strong> {sale.solicitante_nombre || 'Encargada'} ({sale.solicitante_rol || 'encargada'})
                            </p>
                            <p style={{ margin: '4px 0 0 0', color: '#92400e' }}>
                                <strong>Motivo:</strong> {sale.motivo_solicitud}
                            </p>
                        </div>
                    )}

                    {isAnulada && (
                        <div style={{ margin: '15px 0', padding: '12px', background: '#fee2e2', borderLeft: '4px solid #dc2626', borderRadius: '6px', fontSize: '0.9rem' }}>
                            <strong style={{ color: '#dc2626' }}>🚫 Venta Anulada (Stock restituido)</strong>
                            {sale.revisado_por && (
                                <p style={{ margin: '4px 0 0 0', color: '#991b1b' }}>
                                    <strong>Aprobada por:</strong> {sale.revisor_nombre || 'Dueño/Admin'} {sale.revisado_en ? `el ${new Date(sale.revisado_en).toLocaleString('es-PE')}` : ''}
                                </p>
                            )}
                        </div>
                    )}

                    {sale.motivo_rechazo && !isAnulada && (
                        <div style={{ margin: '15px 0', padding: '12px', background: '#fef2f2', borderLeft: '4px solid #ef4444', borderRadius: '6px', fontSize: '0.9rem' }}>
                            <strong style={{ color: '#dc2626' }}>⚠️ Solicitud de anulación previa rechazada</strong>
                            <p style={{ margin: '4px 0 0 0', color: '#991b1b' }}>
                                <strong>Motivo:</strong> {sale.motivo_rechazo}
                            </p>
                        </div>
                    )}
                    
                    <div className="details-table-container">
                        <table className="details-table">
                            <thead>
                                <tr>
                                    <th>Producto</th>
                                    <th>Cant.</th>
                                    <th>P. Unit</th>
                                    <th>Subtotal</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sale.items && sale.items.map((item, idx) => (
                                    <tr key={idx}>
                                        <td>{item.product_name || item.Product?.name || 'Producto genérico'}</td>
                                        <td>{item.unit === 'kg' ? parseFloat(item.quantity) : parseInt(item.quantity)}</td>
                                        <td>S/. {parseFloat(item.unit_price).toFixed(2)}</td>
                                        <td>S/. {parseFloat(item.total_price).toFixed(2)}</td>
                                    </tr>
                                ))}
                                {(!sale.items || sale.items.length === 0) && (
                                    <tr>
                                        <td colSpan="4" style={{textAlign: 'center', padding: '15px', color: '#666'}}>
                                            No hay detalles de productos cargados para esta venta.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                
                <div className="modal-footer">
                    <div className={`status-badge ${isAnulada ? 'cancelled' : isPending ? 'pending' : 'completed'}`} style={isAnulada ? { background: '#fee2e2', color: '#dc2626' } : {}}>
                        {isAnulada ? '🚫 ANULADA' : isPending ? '⏳ PENDIENTE' : '✅ PAGADO'}
                    </div>
                    <div className="total-section">
                        <span>Total:</span>
                        <h2>S/. {parseFloat(sale.total_amount).toFixed(2)}</h2>
                    </div>
                </div>
            </div>
        </div>
    );
};

const SalesHistory = ({ sales, onRefresh }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [selectedSale, setSelectedSale] = useState(null);
    const [showTicket, setShowTicket] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState(null);

    const showToast = (message, type = 'error') => setToast({ message, type });
    
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedSaleForActions, setSelectedSaleForActions] = useState(null);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [saleToVoid, setSaleToVoid] = useState(null);

    // Flujo de anulación en 2 pasos: { mode: 'solicitar'|'aprobar'|'rechazar', sale }
    const [anulacionAction, setAnulacionAction] = useState(null);
    const [motivoAnulacion, setMotivoAnulacion] = useState('');

    const filteredSales = useMemo(() => {
        return sales.filter(sale => {
            // 1. Obtener nombre registrado
            const registeredName = sale.customer_name || sale.Customer?.name || '';
            
            // 2. Obtener nombre manual (Truco Ninja en las notas)
            let manualName = '';
            if (sale.notes && typeof sale.notes === 'string') {
                const match = sale.notes.match(/Cliente:\s*(.+)/i);
                if (match) {
                    manualName = match[1].split('\n')[0].trim();
                }
            }

            const customerDisplayName = registeredName || manualName || 'Consumidor Final';
            
            const displayId = sale.correlativo_local || sale.id;
            const matchesSearch = searchTerm === '' || 
                String(displayId).includes(searchTerm) ||
                customerDisplayName.toLowerCase().includes(searchTerm.toLowerCase());
            
            const dateStr = (sale.created_at || sale.sale_date || sale.date)?.split('T')[0];
            if (!dateStr) return matchesSearch;
            
            const saleDate = new Date(dateStr);
            const start = startDate ? new Date(startDate) : null;
            const end = endDate ? new Date(endDate) : null;
            
            const matchesStartDate = !start || saleDate >= start;
            const matchesEndDate = !end || saleDate <= end;
            
            const currentStatus = sale.payment_status || 'pagado';
            const normalizedStatus = currentStatus.toLowerCase() === 'completed' ? 'pagado' : 
                                     currentStatus.toLowerCase() === 'pending' ? 'pendiente' : 
                                     currentStatus.toLowerCase();
                                     
            const matchesStatus = filterStatus === '' || 
                normalizedStatus === filterStatus.toLowerCase();
            
            return matchesSearch && matchesStartDate && matchesEndDate && matchesStatus;
        });
    }, [sales, searchTerm, startDate, endDate, filterStatus]);

    const totalPages = Math.ceil(filteredSales.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentSales = filteredSales.slice(startIndex, endIndex);

    // ✅ Correcto: useEffect para side effect (resetear página al filtrar)
    useEffect(() => { setCurrentPage(1); }, [searchTerm, startDate, endDate, filterStatus]);

    const columns = [
        { 
            header: 'ID Venta', 
            accessor: 'id',
            render: (id, sale) => {
                const displayId = sale.correlativo_local || id;
                return typeof displayId === 'number' ? `VTA-${String(displayId).padStart(5, '0')}` : displayId;
            }
        },
        { header: 'Fecha', accessor: 'created_at', render: (date) => new Date(date).toLocaleDateString('es-PE', { timeZone: 'America/Lima' }) },
        { 
            header: 'Cliente', 
            accessor: 'customer_name',
            render: (_, sale) => {
                // 1. Cliente Oficial (Registrado en Base de Datos)
                const registeredName = sale.customer_name || sale.Customer?.name;
                if (registeredName) {
                    return (
                        <div className="customer-cell">
                            <span title="Cliente Registrado" style={{marginRight: '5px', color: '#10b981'}}>👤</span>
                            <span style={{ fontWeight: '500' }}>{registeredName}</span>
                        </div>
                    );
                }

                // 2. Cliente Ocasional (Extraído de las notas como ninja)
                let manualName = '';
                if (sale.notes && typeof sale.notes === 'string') {
                    const match = sale.notes.match(/Cliente:\s*(.+)/i);
                    if (match) {
                        manualName = match[1].split('\n')[0].trim();
                    }
                }

                if (manualName) {
                    return (
                        <div className="customer-cell">
                            <span title="Cliente Ocasional" style={{marginRight: '5px', color: '#64748b'}}>👤</span>
                            <span className="ocasional-name" style={{ color: '#64748b', fontStyle: 'italic' }}>
                                {manualName}
                            </span>
                        </div>
                    );
                }

                // 3. Consumidor Final
                return (
                    <div className="customer-cell">
                        <span title="Consumidor Final" style={{marginRight: '5px', color: '#94a3b8'}}>👤</span>
                        <span className="ocasional-name" style={{ color: '#94a3b8', fontStyle: 'italic' }}>
                            Consumidor Final
                        </span>
                    </div>
                );
            }
        },
        { header: 'Total', accessor: 'total_amount', render: (amount) => `S/. ${parseFloat(amount).toFixed(2)}` },
        { 
            header: 'Estado', 
            accessor: 'payment_status',
            render: (status, sale) => {
                // ✅ Cancelado/anulado tiene prioridad — lo determina order_status, no payment_status
                if (sale.order_status === 'cancelado') {
                    return (
                        <span style={{
                            padding: '4px 8px', borderRadius: '12px',
                            fontSize: '12px', fontWeight: '600',
                            backgroundColor: '#fee2e2', color: '#dc2626'
                        }}>
                            ANULADO
                        </span>
                    );
                }

                if (sale.order_status === 'pendiente_anulacion') {
                    return (
                        <span style={{
                            padding: '4px 8px', borderRadius: '12px',
                            fontSize: '12px', fontWeight: '600',
                            backgroundColor: '#fef3c7', color: '#b45309'
                        }}>
                            ⏳ ANULACIÓN PENDIENTE
                        </span>
                    );
                }

                const currentStatus = status || 'pagado';
                const isPending = currentStatus.toLowerCase() === 'pendiente';
                const bgColor = isPending ? '#fef9c3' : '#dcfce7';
                const textColor = isPending ? '#b45309' : '#16a34a';
                const text = isPending ? 'PENDIENTE' : 'COMPLETADO';
                
                return (
                    <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: bgColor,
                        color: textColor
                    }}>
                        {text}
                    </span>
                );
            }
        },

    ];
    
    const loadSaleDetails = async (saleId) => {
        try {
            const response = await salesAPI.getById(saleId);
            if (response.data && response.data.data) {
                const { sale, items } = response.data.data;
                return { ...sale, items: items || [] };
            }
            return null;
        } catch (error) {
            console.error('Error:', error);
            showToast('Error al cargar los detalles de la venta');
            return null;
        }
    };

    const handleShowDetails = async (sale) => {
        setLoading(true);
        const fullSale = await loadSaleDetails(sale.id);
        setLoading(false);
        if (fullSale) {
            setSelectedSale(fullSale);
            setShowDetails(true);
        } else {
            showToast('Error al cargar detalles');
        }
    };

    const handleShowTicket = async (sale) => {
        setLoading(true);
        const fullSale = await loadSaleDetails(sale.id);
        setLoading(false);
        if (fullSale && fullSale.items) {
            setSelectedSale(fullSale);
            setShowTicket(true);
        } else {
            showToast('Error al cargar detalles');
        }
    };

    const handleGeneratePDF = async (sale, openInNewTab) => {
        setLoading(true);
        const fullSale = await loadSaleDetails(sale.id);
        setLoading(false);
        if (fullSale && fullSale.items) {
            generateSalePDF(fullSale, openInNewTab);
        } else {
            showToast('Error al cargar detalles para generar PDF');
        }
    };

    const handleCloseTicket = () => {
        setShowTicket(false);
        setSelectedSale(null);
    };

    const handleVoidSale = (sale) => {
        // Cierra el ActionModal y abre el modal de confirmación propio
        setSelectedSaleForActions(null);
        setSaleToVoid(sale);
    };

    const confirmVoid = async () => {
        if (!saleToVoid) return;
        setLoading(true);
        try {
            const res = await salesAPI.voidSale(saleToVoid.id);
            showToast(res.data?.message || '✅ Venta anulada. Stock restaurado correctamente.', 'success');
            setSaleToVoid(null);
            if (onRefresh) onRefresh();
        } catch (error) {
            showToast('❌ ' + (error.response?.data?.message || 'Error al anular la venta'));
            setSaleToVoid(null);
        } finally {
            setLoading(false);
        }
    };

    // ── Flujo de anulación en 2 pasos ───────────────────────────────────────────
    const openAnulacionAction = (mode, sale) => {
        setSelectedSaleForActions(null);
        setMotivoAnulacion('');
        setAnulacionAction({ mode, sale });
    };

    const closeAnulacionAction = () => {
        setAnulacionAction(null);
        setMotivoAnulacion('');
    };

    const confirmAnulacionAction = async () => {
        if (!anulacionAction) return;
        const { mode, sale } = anulacionAction;

        if (mode !== 'aprobar' && !motivoAnulacion.trim()) {
            showToast('El motivo es obligatorio', 'warning');
            return;
        }

        setLoading(true);
        try {
            let res;
            if (mode === 'solicitar') res = await salesAPI.solicitarAnulacion(sale.id, { motivo: motivoAnulacion.trim() });
            if (mode === 'aprobar')   res = await salesAPI.aprobarAnulacion(sale.id);
            if (mode === 'rechazar')  res = await salesAPI.rechazarAnulacion(sale.id, { motivo_rechazo: motivoAnulacion.trim() });

            showToast(res.data?.message || '✅ Listo', 'success');
            closeAnulacionAction();
            if (onRefresh) onRefresh();
        } catch (error) {
            showToast('❌ ' + (error.response?.data?.message || 'Error al procesar la acción'));
        } finally {
            setLoading(false);
        }
    };

    const handleMarkAsPaid = async (sale) => {
        if (!window.confirm('¿Confirmas que el cliente ya pagó esta deuda?')) return;
        setLoading(true);
        try {
            const amountToPay = parseFloat(sale.balance_due || sale.total_amount);
            await salesAPI.addPayment(sale.id, { 
                amount: amountToPay, 
                notes: 'Cobro de Fiado (Venta Directa)' 
            });
            showToast('✅ Venta marcada como pagada correctamente', 'success');
            // Refrescar datos sin recargar la página
            if (onRefresh) onRefresh();
        } catch (error) {
            console.error('Error al pagar:', error);
            showToast('❌ Error al registrar el pago: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const actions = (sale) => (
        <button 
            onClick={(e) => {
                e.stopPropagation();
                setSelectedSaleForActions(sale);
            }}
            className="kebab-btn"
            title="Más opciones"
        >
            ⋮
        </button>
    );

    const clearFilters = () => {
        setSearchTerm('');
        setStartDate('');
        setEndDate('');
        setFilterStatus('');
        setCurrentPage(1);
    };

    const goToPage = (page) => setCurrentPage(page);
    const goToPreviousPage = () => { if (currentPage > 1) setCurrentPage(currentPage - 1); };
    const goToNextPage = () => { if (currentPage < totalPages) setCurrentPage(currentPage + 1); };

    return (
        <div className="history-container">
            {loading && (
                <div className="loading-overlay">
                    <div className="loading-box">Cargando detalles...</div>
                </div>
            )}

            <div className="filters-bar">
                <div className="filter-group filter-search">
                    <label>Buscar (ID o Cliente)</label>
                    <input 
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Buscar por ID o nombre..."
                    />
                </div>
                
                <div className="filter-group">
                    <label>📅 Fecha Inicio</label>
                    <input 
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                    />
                </div>
                
                <div className="filter-group">
                    <label>📅 Fecha Fin</label>
                    <input 
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                    />
                </div>
                
                <div className="filter-group">
                    <label>Estado</label>
                    <select 
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        <option value="">Todos</option>
                        <option value="pagado">Completado / Pagado</option>
                        <option value="pendiente">Pendiente</option>
                        <option value="anulado">Cancelado / Anulado</option>
                    </select>
                </div>
                
                <div className="filter-group filter-btn-group">
                    <button onClick={clearFilters} className="btn-clear">
                        🗑️ Limpiar
                    </button>
                </div>
            </div>

            <div className="pagination-info">
                <p>Mostrando {startIndex + 1}-{Math.min(endIndex, filteredSales.length)} de {filteredSales.length} ventas</p>
                <div className="items-per-page">
                    <label>Mostrar:</label>
                    <select 
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                    <input 
                        type="number"
                        placeholder="..."
                        min="1"
                        max="500"
                        style={{ width: '60px', marginLeft: '5px', padding: '4px', borderRadius: '4px', border: '1px solid #ccc' }}
                        onBlur={(e) => {
                            const val = Number(e.target.value);
                            if (val >= 1 && val <= 500) {
                                setItemsPerPage(val);
                                setCurrentPage(1);
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                const val = Number(e.target.value);
                                if (val >= 1 && val <= 500) {
                                    setItemsPerPage(val);
                                    setCurrentPage(1);
                                }
                            }
                        }}
                    />
                    <span style={{ marginLeft: '5px' }}>por página</span>
                </div>
            </div>

            <div className="history-table-wrapper">
                <CustomTable columns={columns} data={currentSales} actions={actions} />
            </div>

            {totalPages > 1 && (
                <div className="pagination-controls">
                    <button 
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        className="btn-page-nav"
                    >
                        ← Anterior
                    </button>

                    <div className="page-numbers">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                            if (page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2)) {
                                return (
                                    <button
                                        key={page}
                                        onClick={() => goToPage(page)}
                                        className={`btn-page ${page === currentPage ? 'active' : ''}`}
                                    >
                                        {page}
                                    </button>
                                );
                            } else if (page === currentPage - 3 || page === currentPage + 3) {
                                return <span key={page} className="page-dots">...</span>;
                            }
                            return null;
                        })}
                    </div>

                    <button 
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className="btn-page-nav"
                    >
                        Siguiente →
                    </button>
                </div>
            )}

            {showTicket && selectedSale && (
                <SaleTicket sale={selectedSale} onClose={handleCloseTicket} />
            )}
            
            {showDetails && selectedSale && (
                <SaleDetailsModal sale={selectedSale} onClose={() => { setShowDetails(false); setSelectedSale(null); }} />
            )}

            {selectedSaleForActions && (
                <ActionModal 
                    isOpen={!!selectedSaleForActions}
                    onClose={() => setSelectedSaleForActions(null)}
                    title="Acciones de Venta"
                    itemName={`Venta #${typeof selectedSaleForActions.correlativo_local === 'number' ? `VTA-${String(selectedSaleForActions.correlativo_local).padStart(5, '0')}` : (selectedSaleForActions.correlativo_local || selectedSaleForActions.id)}`}
                    itemSubtitle={`Cliente: ${selectedSaleForActions.customer_name || selectedSaleForActions.Customer?.name || (typeof selectedSaleForActions.notes === 'string' ? (selectedSaleForActions.notes.match(/Cliente:\s*(.+)/i) ? selectedSaleForActions.notes.match(/Cliente:\s*(.+)/i)[1].split('\n')[0].trim() : '') : '') || 'Consumidor Final'}`}
                    actions={[
                        {
                            title: 'Ver Detalles',
                            icon: '👁️',
                            variant: 'primary',
                            onClick: () => handleShowDetails(selectedSaleForActions)
                        },
                        {
                            title: 'Ver Ticket',
                            icon: '🎫',
                            variant: 'default',
                            onClick: () => handleShowTicket(selectedSaleForActions)
                        },
                        {
                            title: 'Ver PDF',
                            icon: '📄',
                            variant: 'default',
                            onClick: () => handleGeneratePDF(selectedSaleForActions, true)
                        },
                        {
                            title: 'Descargar PDF',
                            icon: '💾',
                            variant: 'default',
                            onClick: () => handleGeneratePDF(selectedSaleForActions, false)
                        },
                        ...((selectedSaleForActions.payment_status?.toLowerCase() === 'pendiente' || (selectedSaleForActions.balance_due && parseFloat(selectedSaleForActions.balance_due) > 0)) ? [{
                            title: 'Marcar como Pagado',
                            icon: '💰',
                            variant: 'success',
                            onClick: () => handleMarkAsPaid(selectedSaleForActions)
                        }] : []),
                        // Dueño: anulación directa en 1 paso (como ya existía)
                        ...(isAdmin() &&
                            selectedSaleForActions.order_status === 'entregado' &&
                            selectedSaleForActions.payment_status !== 'pendiente'
                          ? [{
                              title: 'Anular Venta',
                              icon: '🚫',
                              variant: 'danger',
                              onClick: () => handleVoidSale(selectedSaleForActions)
                          }]
                          : []),
                        // Encargada: solicita anulación (2 pasos, requiere aprobación del Dueño)
                        ...(isEncargada() &&
                            selectedSaleForActions.order_status === 'entregado' &&
                            selectedSaleForActions.payment_status !== 'pendiente'
                          ? [{
                              title: 'Solicitar Anulación',
                              icon: '📝',
                              variant: 'danger',
                              onClick: () => openAnulacionAction('solicitar', selectedSaleForActions)
                          }]
                          : []),
                        // Dueño: aprobar/rechazar una solicitud pendiente de la Encargada
                        ...(isAdmin() && selectedSaleForActions.order_status === 'pendiente_anulacion'
                          ? [
                              {
                                  title: 'Aprobar Anulación',
                                  icon: '✅',
                                  variant: 'success',
                                  onClick: () => openAnulacionAction('aprobar', selectedSaleForActions)
                              },
                              {
                                  title: 'Rechazar Anulación',
                                  icon: '❌',
                                  variant: 'danger',
                                  onClick: () => openAnulacionAction('rechazar', selectedSaleForActions)
                              }
                            ]
                          : [])
                    ]}
                />
            )}

            {/* Modal de confirmación de anulación */}
            {saleToVoid && (
                <div className="modal-overlay" style={{ zIndex: 999999 }} onClick={() => setSaleToVoid(null)}>
                    <div className="modal-content" style={{ maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
                        <div className="modal-header" style={{ borderBottom: '3px solid #ef4444' }}>
                            <h2 style={{ color: '#dc2626', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                ⚠️ Anular Venta
                            </h2>
                            <button className="close-button" onClick={() => setSaleToVoid(null)}>×</button>
                        </div>
                        <div className="modal-body">
                            <p style={{ margin: '0 0 12px 0', color: '#1e293b', fontWeight: '600', fontSize: '1rem' }}>
                                ¿Anular{' '}
                                <span style={{ color: '#dc2626' }}>
                                    {typeof saleToVoid.correlativo_local === 'number'
                                        ? `VTA-${String(saleToVoid.correlativo_local).padStart(5, '0')}`
                                        : (saleToVoid.correlativo_local || saleToVoid.id)}
                                </span>
                                ?
                            </p>
                            <p style={{ margin: '0 0 24px 0', color: '#64748b', fontSize: '0.9rem', lineHeight: '1.5' }}>
                                El stock de todos los productos será restaurado.
                                <br />
                                <strong style={{ color: '#dc2626' }}>Esta acción no se puede deshacer.</strong>
                            </p>
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <button
                                    onClick={() => setSaleToVoid(null)}
                                    disabled={loading}
                                    style={{
                                        flex: 1, padding: '11px', border: '1px solid #cbd5e1',
                                        background: 'white', color: '#475569', fontWeight: '600',
                                        borderRadius: '8px', cursor: 'pointer', fontSize: '0.95rem'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={confirmVoid}
                                    disabled={loading}
                                    style={{
                                        flex: 1, padding: '11px', border: 'none',
                                        background: loading ? '#fca5a5' : '#dc2626',
                                        color: 'white', fontWeight: '700',
                                        borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer',
                                        fontSize: '0.95rem', transition: 'background 0.2s'
                                    }}
                                >
                                    {loading ? 'Anulando...' : 'Sí, anular venta'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal del flujo de anulación en 2 pasos */}
            {anulacionAction && (
                <AnulacionActionModal
                    mode={anulacionAction.mode}
                    sale={anulacionAction.sale}
                    motivo={motivoAnulacion}
                    setMotivo={setMotivoAnulacion}
                    onConfirm={confirmAnulacionAction}
                    onClose={closeAnulacionAction}
                    loading={loading}
                />
            )}

            {/* Toast de notificaciones */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
};

export default SalesHistory;